// ── La corriente, del lado del navegador ─────────────────────────────────────
//
// Una sola por pestaña para todo el ERP. Los avisos, el correo y las
// conversaciones se suscriben a SUS eventos con `on()`, y nadie abre un segundo
// EventSource: cada uno gasta una de las seis conexiones por origen que tiene
// un navegador en HTTP/1.1.
//
// Lo difícil no es abrirla, es que un teléfono en una furgoneta la abre y la
// pierde quince veces en un trayecto, se duerme en el bolsillo y se despierta
// con una corriente que parece abierta y no lo está. Tres cosas lo resuelven:
//
//   1. UN solo motor de reintento. `EventSource` reintenta solo cada ~3 s en
//      cuanto se cae; si además hay un temporizador propio, se suman los dos y
//      se come el cupo del servidor (pasó en `notifications`, su CHANGELOG lo
//      cuenta). Al primer error se CIERRA —lo único que apaga el nativo— y se
//      reabre desde aquí con espera creciente.
//   2. Un vigilante. El servidor manda un latido cada 25 s; si pasan 70 sin
//      ninguno, la corriente está muerta aunque el navegador no lo sepa (el
//      caso típico: el móvil se durmió y el proxy cortó por el camino). Se
//      reabre.
//   3. Volver en el acto cuando vuelve la red o la pantalla: `online`,
//      `visibilitychange`, `pageshow` y `resume`. Sin esto, alguien que sale de
//      un túnel espera hasta el siguiente escalón de la escalera.
//
// Y lo que NO hace: garantizar la entrega. Lo que llega mientras la corriente
// está caída se pierde, y está bien: quien no puede perder nada (un mensaje) lo
// guarda y lo reconcilia en `onOpen`, que se llama en CADA apertura.

import { ref, computed } from 'vue'
import { api } from '@general/ui-kit'

// ── La escalera de reintentos ────────────────────────────────────────────────
// 2 s · 4 · 8 · 16 · 30 · 30 …, más hasta un segundo de azar para que veinte
// pestañas que perdieron la red a la vez no vuelvan en el mismo instante. En la
// peor ventana de cinco minutos son catorce aperturas, y el servidor deja
// sesenta (y devuelve las que vivieron). El test lo comprueba leyendo los dos
// ficheros: `platform/realtime/tests/reconnect.test.js`.
const RETRY_MIN_MS = 2_000
const RETRY_MAX_MS = 30_000

// Cuánto silencio es una corriente muerta: casi tres latidos del servidor.
const STALE_AFTER_MS = 70_000
// Al volver a la pantalla, lo que se considera «demasiado viejo para fiarse».
const RESUME_STALE_MS = 40_000

const jitter = () => Math.floor(Math.random() * 1_000)

function createRealtime({ path = '/realtime/stream' } = {}) {
  /** idle · connecting · open · retrying · offline */
  const state = ref('idle')
  const connected = computed(() => state.value === 'open')
  const lastEventAt = ref(0)
  const opens = ref(0)

  const handlers = new Map()        // evento → Set(handler)
  const openHandlers = new Set()    // se llaman en cada apertura
  const stateHandlers = new Set()

  let source = null
  let retryTimer = null
  let watchdog = null
  let retryMs = RETRY_MIN_MS
  let attempts = 0                  // la apertura en curso: una anterior que vuelve tarde no cuenta
  let wanted = false
  const attached = new Set()        // eventos con oyente en el `source` actual

  function setState(next) {
    if (state.value === next) return
    state.value = next
    for (const handler of stateHandlers) {
      try { handler(next) } catch { /* un oyente roto no para la corriente */ }
    }
  }

  function dispatch(event, raw) {
    lastEventAt.value = Date.now()
    const set = handlers.get(event)
    if (!set?.size) return
    let data = null
    try { data = raw ? JSON.parse(raw) : null } catch { return }
    for (const handler of set) {
      try { handler(data) } catch (error) {
        // Un consumidor que se rompe no puede cortar la corriente de los demás.
        console.warn(`[realtime] handler for '${event}' failed`, error)
      }
    }
  }

  function attach(event) {
    if (!source || attached.has(event)) return
    attached.add(event)
    source.addEventListener(event, (message) => dispatch(event, message.data))
  }

  async function open() {
    closeSource()
    if (!wanted) return
    setState(opens.value ? 'retrying' : 'connecting')

    // EventSource no manda cabeceras, así que la credencial va en la URL: un
    // pase de un solo uso y 60 s que se pide con la cabecera de siempre (y no
    // el token de sesión, que acababa escrito en el registro del proxy). El
    // cliente de la API renueva el token si hace falta antes de pedirlo.
    const attempt = ++attempts
    let url
    try {
      const { ticket } = await api.post('/realtime/ticket')
      url = api.url(`${path}?ticket=${encodeURIComponent(ticket)}`)
    } catch {
      // Sin red o sin sesión: al siguiente escalón de la escalera.
      if (attempt === attempts && wanted) fail()
      return
    }
    // Mientras se pedía el pase pudo cerrarse la corriente u otra apertura
    // tomar el relevo: la última es la que vale.
    if (!wanted || attempt !== attempts) return

    const current = new EventSource(url)
    source = current
    attached.clear()

    current.addEventListener('connected', () => {
      if (source !== current) return
      lastEventAt.value = Date.now()
      // La escalera se reinicia SOLO cuando abrió de verdad: un 429 también
      // «contesta», y reiniciarla con cualquier respuesta dejaría al cliente
      // llamando cada dos segundos para siempre.
      retryMs = RETRY_MIN_MS
      opens.value += 1
      setState('open')
      const first = opens.value === 1
      for (const handler of openHandlers) {
        try { handler({ first }) } catch (error) { console.warn('[realtime] open handler failed', error) }
      }
    })
    current.addEventListener('ping', () => { lastEventAt.value = Date.now() })
    current.addEventListener('bye', () => { if (source === current) fail() })
    for (const event of handlers.keys()) attach(event)

    current.onerror = () => { if (source === current) fail() }
  }

  function fail() {
    // Cerrar es lo que apaga el reintento nativo. Sin esto el navegador sigue
    // llamando cada ~3 s por su cuenta y la espera de abajo no sirve de nada.
    closeSource()
    if (!wanted) return
    setState(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'retrying')
    if (!retryTimer) {
      retryTimer = setTimeout(() => { retryTimer = null; open() }, retryMs + jitter())
      retryMs = Math.min(retryMs * 2, RETRY_MAX_MS)
    }
  }

  function closeSource() {
    if (source) {
      source.onerror = null
      source.close()
    }
    source = null
    attached.clear()
  }

  function startWatchdog() {
    if (watchdog) return
    watchdog = setInterval(() => {
      if (!wanted || state.value !== 'open') return
      if (Date.now() - lastEventAt.value > STALE_AFTER_MS) {
        console.info('[realtime] stream went silent, reopening')
        fail()
      }
    }, 10_000)
  }

  /** Ya, sin esperar el siguiente escalón: ha vuelto la red o la pantalla. */
  function reconnectNow() {
    if (!wanted) return
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
    retryMs = RETRY_MIN_MS
    open()
  }

  function onResume() {
    if (!wanted) return
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    const stale = Date.now() - lastEventAt.value > RESUME_STALE_MS
    if (state.value !== 'open' || stale) reconnectNow()
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onResume)
    window.addEventListener('offline', () => { if (wanted && state.value !== 'open') setState('offline') })
    window.addEventListener('pageshow', onResume)
    document.addEventListener('visibilitychange', onResume)
    // Chrome en Android congela la pestaña en segundo plano y la despierta con
    // `resume`: la corriente de antes del congelado ya no existe.
    document.addEventListener('resume', onResume)
  }

  return {
    state,
    connected,
    lastEventAt,

    /** Abrir (o seguir abierta). Idempotente. */
    connect() {
      if (wanted && source) return
      wanted = true
      startWatchdog()
      open()
    },

    /** Cerrar y no volver: fin de sesión. */
    disconnect() {
      wanted = false
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
      closeSource()
      opens.value = 0
      setState('idle')
    },

    /** Reabrir ahora: el token ha cambiado, o alguien lo pide a mano. */
    reconnect() {
      if (!wanted) return
      reconnectNow()
    },

    /**
     * Escuchar un evento. Devuelve la función que deja de escucharlo.
     * @param {string} event  p. ej. 'notification', 'comms:message'
     */
    on(event, handler) {
      if (!handlers.has(event)) handlers.set(event, new Set())
      handlers.get(event).add(handler)
      attach(event)
      return () => handlers.get(event)?.delete(handler)
    },

    /**
     * Cada vez que la corriente abre —la primera y cada reconexión—. Es donde
     * quien no puede perder nada pregunta qué se perdió mientras estaba caída.
     */
    onOpen(handler) {
      openHandlers.add(handler)
      return () => openHandlers.delete(handler)
    },

    onState(handler) {
      stateHandlers.add(handler)
      return () => stateHandlers.delete(handler)
    },
  }
}

// Una por pestaña: la comparten todos los que la usan.
export const realtime = createRealtime()

export { createRealtime, RETRY_MIN_MS, RETRY_MAX_MS, STALE_AFTER_MS }
